/**
 * ConfirmModal organism - standardized confirmation dialog
 * Provides consistent confirmation UX across the application
 */

import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  HStack,
  VStack,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import {
  FiAlertTriangle,
  FiTrash2,
  FiInfo,
  FiHelpCircle,
} from 'react-icons/fi';
import { Button } from '../../atoms/buttons/Button';
import { Text } from '../../atoms/typography/Text';
import { ModalProps, ComponentVariant } from '../../types';

export interface ConfirmModalProps extends ModalProps {
  /** Type of confirmation */
  type?: 'danger' | 'warning' | 'info' | 'question';
  /** Main message */
  message: string;
  /** Additional description */
  description?: string;
  /** Confirm button text */
  confirmText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Callback when confirmed */
  onConfirm: () => void | Promise<void>;
  /** Whether confirm action is loading */
  loading?: boolean;
}

const typeConfig = {
  danger: {
    icon: FiTrash2,
    iconColor: 'red.500',
    confirmVariant: 'danger' as ComponentVariant,
  },
  warning: {
    icon: FiAlertTriangle,
    iconColor: 'orange.500',
    confirmVariant: 'warning' as ComponentVariant,
  },
  info: {
    icon: FiInfo,
    iconColor: 'blue.500',
    confirmVariant: 'primary' as ComponentVariant,
  },
  question: {
    icon: FiHelpCircle,
    iconColor: 'gray.500',
    confirmVariant: 'primary' as ComponentVariant,
  },
};

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  type = 'question',
  message,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  loading = false,
  isOpen,
  onClose,
  title = 'Confirm Action',
  ...props
}) => {
  const config = typeConfig[type];
  const iconBg = useColorModeValue(
    `${config.iconColor.split('.')[0]}.50`,
    `${config.iconColor.split('.')[0]}.900`,
  );

  const handleConfirm = async () => {
    try {
      await onConfirm();
    } catch (error) {
      console.error('Confirmation action failed:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={2}>{title}</ModalHeader>

        <ModalBody py={6}>
          <HStack spacing={4} align="flex-start">
            <VStack
              bg={iconBg}
              p={3}
              borderRadius="full"
              color={config.iconColor}
              minWidth="fit-content"
            >
              <Icon as={config.icon} boxSize={6} />
            </VStack>

            <VStack align="flex-start" spacing={3} flex="1">
              <Text fontSize="lg" fontWeight="medium">
                {message}
              </Text>

              {description && (
                <Text variant="caption" lineHeight="1.5">
                  {description}
                </Text>
              )}
            </VStack>
          </HStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="secondary" onClick={onClose} isDisabled={loading}>
              {cancelText}
            </Button>
            <Button
              variant={config.confirmVariant}
              onClick={handleConfirm}
              loading={loading}
            >
              {confirmText}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
